import type { Query, QueryClient } from "@tanstack/react-query";

/* ══════════════════════════════════════════════════════════════════
   Ein verworfener Vorgang wird nicht mehr gefragt.

   N6-02 (Nachprüfung Runde 6, 04.10.2026): Nach „Entwurf verwerfen" holte
   die Seite den eben gelöschten Entwurf noch einmal — `GET /vorgaenge/AG-…`
   zweimal, `…/notizen` einmal, alle drei 404 in der Konsole. Zwei Wege
   führten dahin:

   1. Das Auffrischen nach dem Verwerfen (`aufnahmeFrisch`, Befund 40) traf
      mit dem Präfix `["vorgang"]` auch den Vorgang selbst — und die Seite,
      die ihn noch zeigte, holte ihn sofort neu.
   2. Das Blatt sprang vor dem Wechsel auf `/angebote` in die Hauptansicht
      zurück und baute dabei Notizen und Vorgang neu auf; deren Stand war
      älter als 30 Sekunden und wurde beim Aufbau nachgeholt.

   Jetzt gilt die Reihenfolge: **stilllegen — weggehen — vergessen.** Was
   noch unterwegs ist, wird abgebrochen; aufgefrischt wird alles außer dem
   verworfenen Vorgang; die Seite geht, ohne vorher etwas neu aufzubauen; und
   erst danach verschwinden seine Abfragen aus dem Bestand. Eine Abfrage,
   die noch jemand beobachtet, bleibt stehen — sie zu entfernen hieße, dass
   der Beobachter sie beim nächsten Zeichnen neu anlegt und holt.
   ══════════════════════════════════════════════════════════════════ */

/**
 * Gehört diese Abfrage zum Vorgang `nr`? Eine Vorgangsnummer kommt in einem
 * Schlüssel nur als sie selbst vor — `["vorgang", nr]`, `["vorgang", nr,
 * "fassungen"]`, `["notizen", nr]`, `["positionen", nr, …]`,
 * `["belegtexte", nr]`, `["protokolle", nr]`, `["zeiten", "vorgang", nr]`.
 */
export function gehoertZumVorgang(schluessel: readonly unknown[], nr: string): boolean {
  return nr !== "" && schluessel.includes(nr);
}

/** Für `invalidateQueries`: alles, nur nicht der verworfene Vorgang. */
export const ohneVorgang = (nr: string) => (q: Query) => !gehoertZumVorgang(q.queryKey, nr);

/** Bricht ab, was zum Vorgang noch unterwegs ist — die Antwort wäre ein 404. */
export function vorgangStilllegen(k: QueryClient, nr: string): Promise<void> {
  return k.cancelQueries({ predicate: (q) => gehoertZumVorgang(q.queryKey, nr) });
}

/**
 * Nimmt die Abfragen des Vorgangs aus dem Bestand — **nach** dem Weggehen.
 * Wer zurückblättert, sieht dann keinen gelöschten Entwurf aus dem Speicher,
 * sondern die ehrliche Antwort des Servers.
 */
export function vorgangVergessen(k: QueryClient, nr: string): void {
  k.removeQueries({
    predicate: (q) => gehoertZumVorgang(q.queryKey, nr) && q.getObserversCount() === 0,
  });
}
